/**
 * Wishlist (Hometech). Saved on the shopper's device (localStorage), no account needed.
 *
 * <s-wishlist-toggle data-handle data-title data-image data-url>  wraps one <button aria-pressed>.
 * <s-wishlist-count>  header badge, shows the number of saved products.
 * <s-wishlist-drawer data-i18n='{...}'>  wraps the <dialog id="wishlist-drawer"> listing saved products.
 *   Prices and availability are refreshed from /products/<handle>?view=compare when the drawer opens.
 */
const STORE_KEY = 'hometech-wishlist';
const EVENT = 'hometech:wishlist-change';
const MAX_ITEMS = 50;

function readList() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
  } catch (_) {
    return [];
  }
}

function writeList(list) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS))); } catch (_) { /* ignore */ }
  document.dispatchEvent(new CustomEvent(EVENT));
}

function i18n() {
  const drawer = document.querySelector('s-wishlist-drawer');
  try { return JSON.parse(drawer?.dataset.i18n || '{}'); } catch (_) { return {}; }
}

function fill(template, values) {
  return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (key in values ? values[key] : ''));
}

function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null || value === false) continue;
    if (key === 'text') node.textContent = value;
    else node.setAttribute(key, value === true ? '' : value);
  }
  for (const child of [].concat(children)) if (child) node.append(child);
  return node;
}

function onChange(callback) {
  document.addEventListener(EVENT, callback);
  window.addEventListener('storage', (event) => { if (event.key === STORE_KEY) callback(); });
}

class SWishlistToggle extends HTMLElement {
  #button;
  #onChange = () => this.#render();

  #bound = false;
  #lastToggle = 0;

  // Bind once: a reconnected card (grid re-render) must not stack a second click handler.
  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#bound) {
      this.#bound = true;
      this.#button?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const now = Date.now();
        if (now - this.#lastToggle < 400) return;
        this.#lastToggle = now;
        this.#toggle();
      });
      onChange(this.#onChange);
    }
    this.#render();
  }


  #toggle() {
    const { handle, title, image, url } = this.dataset;
    const list = readList();
    const exists = list.some((item) => item.handle === handle);
    writeList(exists ? list.filter((item) => item.handle !== handle) : [{ handle, title, image, url }, ...list]);
  }

  #render() {
    if (!this.#button) return;
    const t = i18n();
    const saved = readList().some((item) => item.handle === this.dataset.handle);
    this.#button.setAttribute('aria-pressed', String(saved));
    this.#button.setAttribute('aria-label', fill(saved ? t.remove_label : t.add_label, { title: this.dataset.title }));
    const label = this.querySelector('[data-wishlist-label]');
    if (label) label.textContent = saved ? t.added : t.add;
  }
}

class SWishlistCount extends HTMLElement {
  #onChange = () => this.#render();

  connectedCallback() {
    onChange(this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#onChange);
  }

  #render() {
    const count = readList().length;
    const badge = this.querySelector('[data-wishlist-badge]');
    if (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    }
    const trigger = this.querySelector('button');
    if (trigger) trigger.setAttribute('aria-label', fill(i18n().open, { count }));
  }
}

class SWishlistDrawer extends HTMLElement {
  #dialog;
  #details = new Map();
  #onChange = () => { if (this.#dialog?.open) this.#render(); };

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.querySelector('[data-wishlist-clear]')?.addEventListener('click', () => writeList([]));
    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    });
    // Refresh whenever the dialog opens (opened by the header heart via commandfor/show-modal).
    this.#dialog?.addEventListener('toggle', (event) => { if (event.newState === 'open') this.#render(true); });
    this.#dialog?.addEventListener('command', (event) => { if (event.command === 'show-modal') this.#render(true); });
    onChange(this.#onChange);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#onChange);
  }

  async #detail(handle) {
    if (this.#details.has(handle)) return this.#details.get(handle);
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const dataUrl = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
    dataUrl.searchParams.set('view', 'compare');
    const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewId) dataUrl.searchParams.set('preview_theme_id', previewId);
    const promise = fetch(dataUrl.pathname + dataUrl.search, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return null;
        try { return JSON.parse(text); } catch (_) { /* preview scripts may wrap the JSON */ }
        const start = text.indexOf('{');
        const end = text.lastIndexOf('}');
        return start > -1 && end > start ? JSON.parse(text.slice(start, end + 1)) : null;
      })
      .catch(() => null);
    this.#details.set(handle, promise);
    return promise;
  }

  async #render(refresh = false) {
    const t = i18n();
    const list = readList();
    const body = this.querySelector('[data-wishlist-items]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const footer = this.querySelector('[data-wishlist-footer]');
    const count = this.querySelector('[data-wishlist-count]');
    if (count) count.textContent = list.length ? `(${list.length})` : '';
    if (empty) empty.hidden = list.length > 0;
    if (footer) footer.hidden = list.length === 0;
    if (!body) return;
    if (refresh) this.#details.clear();

    body.replaceChildren(...list.map((item) => {
      const remove = el('button', {
        type: 'button',
        class: 'wishlist-remove',
        'aria-label': fill(t.remove_label, { title: item.title }),
      }, [el('span', { 'aria-hidden': 'true', text: '×' })]);
      remove.addEventListener('click', () => writeList(readList().filter((i) => i.handle !== item.handle)));
      const meta = el('p', { class: 'wishlist-meta', 'data-wishlist-meta': item.handle });
      return el('li', { class: 'wishlist-item' }, [
        el('a', { href: item.url, class: 'wishlist-thumb', tabindex: '-1', 'aria-hidden': 'true' }, [
          item.image ? el('img', { src: item.image, alt: '', width: '80', height: '80', loading: 'lazy' }) : null,
        ]),
        el('div', { class: 'wishlist-copy' }, [
          el('a', { href: item.url, class: 'wishlist-title', text: item.title }),
          meta,
        ]),
        remove,
      ]);
    }));

    for (const item of list) {
      this.#detail(item.handle).then((product) => {
        const meta = body.querySelector(`[data-wishlist-meta="${CSS.escape(item.handle)}"]`);
        if (!meta || !product) return;
        meta.replaceChildren(
          el('span', { class: 'wishlist-price', text: product.price }),
          product.compare_at ? el('s', { class: 'wishlist-was', text: product.compare_at }) : null,
          el('span', { class: product.available ? 'wishlist-stock' : 'wishlist-stock is-out', text: product.available ? t.in_stock : t.sold_out }),
        );
      });
    }
  }
}

if (!customElements.get('s-wishlist-toggle')) customElements.define('s-wishlist-toggle', SWishlistToggle);
if (!customElements.get('s-wishlist-count')) customElements.define('s-wishlist-count', SWishlistCount);
if (!customElements.get('s-wishlist-drawer')) customElements.define('s-wishlist-drawer', SWishlistDrawer);
